import { useState } from 'react';
import type { FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, formatFieldErrors } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import './auth.css';

interface PublicTeam {
  id: number;
  name: string;
  ageGroup: string;
}

/** Matches RegisterRequestValidator.MaxTeamRequests — the server rejects more than this. */
const MAX_TEAMS = 10;

export function RegisterPage() {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  // One entry per dropdown row; '' is a row the coach has added but not picked yet. The ids
  // actually submitted are the non-empty rows.
  const [teamRows, setTeamRows] = useState<Array<number | ''>>(['']);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // Anonymous: the picker exists before the account does. A failed load is not fatal — the
  // admin can still link teams by hand, so the section simply hides itself.
  const teams = useQuery({
    queryKey: ['public', 'teams'],
    queryFn: () => api<PublicTeam[]>('/api/public/teams'),
    retry: false,
  });

  const teamIds = teamRows.filter((row): row is number => row !== '');

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await api('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password, displayName, teamIds }),
      });
      setDone(true);
    } catch (err) {
      setError(formatFieldErrors(err));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="auth-page">
        <Card className="auth-card" title="Check your email">
          <div className="auth-info">
            We&apos;ve sent a confirmation link to <strong>{email}</strong>. After you confirm, a club
            admin will review and approve your account
            {teamIds.length > 0 ? ' and the teams you selected' : ''}.
          </div>
          <Link className="auth-inline-link" to="/login">
            Back to sign in
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <Card
        className="auth-card"
        title="Create a coach account"
        subtitle="Accounts are approved by a club admin before first use"
      >
        {error ? <div className="auth-error">{error}</div> : null}
        <form onSubmit={submit}>
          <Input
            label="Your name"
            autoComplete="name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
            minLength={2}
          />
          <Input
            label="Email address"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Input
            label="Password"
            type="password"
            autoComplete="new-password"
            hint="At least 10 characters with upper & lower case, a number and a symbol"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={10}
          />
          <Input
            label="Confirm password"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
          {teams.data && teams.data.length > 0 ? (
            <TeamPicker teams={teams.data} rows={teamRows} onChange={setTeamRows} />
          ) : null}
          <Button type="submit" loading={busy} className="auth-full-width">
            Create account
          </Button>
          <div className="auth-links">
            <span />
            <Link to="/login">Already have an account?</Link>
          </div>
        </form>
      </Card>
    </div>
  );
}

/**
 * Optional team selection: one dropdown per team, grouped inside by age group (the server
 * returns ladder order), with "Add another team" for coaches who run more than one. Selecting
 * a team asks to coach it — the admin grants or refuses each one when they approve the account.
 */
function TeamPicker({
  teams,
  rows,
  onChange,
}: {
  teams: PublicTeam[];
  rows: Array<number | ''>;
  onChange: (rows: Array<number | ''>) => void;
}) {
  const groups: Array<[string, PublicTeam[]]> = [];
  for (const team of teams) {
    const last = groups[groups.length - 1];
    if (last && last[0] === team.ageGroup) last[1].push(team);
    else groups.push([team.ageGroup, [team]]);
  }

  const setRow = (index: number, value: number | '') =>
    onChange(rows.map((row, i) => (i === index ? value : row)));
  const removeRow = (index: number) => onChange(rows.filter((_, i) => i !== index));

  const atLimit = rows.length >= MAX_TEAMS;
  const lastRowEmpty = rows[rows.length - 1] === '';

  return (
    <fieldset className="auth-teams">
      <legend>Which teams do you coach?</legend>
      <p className="auth-teams-hint">
        Optional — a club admin approves these when they approve your account. Leave it blank if
        your team isn&apos;t listed and an admin will sort it out.
      </p>
      {rows.map((value, index) => (
        <div key={index} className="auth-team-row">
          <select
            className="ui-select"
            aria-label={rows.length === 1 ? 'Team' : `Team ${index + 1}`}
            value={value}
            onChange={(e) => setRow(index, e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">Select a team…</option>
            {groups.map(([ageGroup, groupTeams]) => (
              <optgroup key={ageGroup} label={ageGroup}>
                {groupTeams.map((team) => (
                  <option
                    key={team.id}
                    value={team.id}
                    disabled={team.id !== value && rows.includes(team.id)}
                  >
                    {team.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {rows.length > 1 ? (
            <button
              type="button"
              className="auth-team-remove"
              aria-label={`Remove team ${index + 1}`}
              onClick={() => removeRow(index)}
            >
              <X size={16} aria-hidden />
            </button>
          ) : null}
        </div>
      ))}
      {atLimit ? (
        <p className="auth-teams-hint auth-teams-hint-after">
          That&apos;s the maximum — an admin can add more teams after you&apos;re approved.
        </p>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          disabled={lastRowEmpty}
          onClick={() => onChange([...rows, ''])}
        >
          + Add another team
        </Button>
      )}
    </fieldset>
  );
}
